/**
 * /admin/page-backgrounds — تحكّم بخلفيّة كلّ صفحة عامّة (صورة + حجم + كروب)
 * مع variants لكلّ (جهاز × سوق).
 */
import * as React from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { ArrowLeft, Upload, Trash2, Loader2, Eye, EyeOff, MapPin } from "lucide-react";
import { DropZone } from "@/components/admin/DropZone";
import { HotspotsEditorDialog } from "@/components/admin/HotspotsEditorDialog";
import { toast } from "@/lib/toast";
import { useAdminGate } from "@/hooks/useAdminGate";
import { supabase } from "@/integrations/supabase/client";
import { bustUrl } from "@/lib/storage-bust";
import { compressImageBeforeUpload, HERO_COMPRESS_OPTIONS } from "@/lib/compress-image";
import { fetchCollections } from "@/lib/shopify";
import { SiteShell } from "@/components/SiteShell";
import { AdminNav } from "@/components/admin/AdminNav";
import { PageHeroCtasPanel } from "@/components/admin/PageHeroCtasPanel";
import { HeroMediaSection } from "@/components/admin/HeroMediaSection";
import { PageElementsCenter } from "@/components/admin/PageElementsCenter";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import {
  makeVariantKey,
  PAGE_KEYS,
  DEVICE_KEYS,
  MARKET_KEYS,
  type PageBgSize,
  type PageBgMode,
  type PageKey,
  type DeviceKey,
  type MarketKey,
  type PageBgSettings,
  type PageVariants,
} from "@/utils/pageBackgrounds.shared";
import { getPageBackgrounds, setPageBackground } from "@/utils/pageBackgrounds.functions";
import {
  getPageHeroImages,
  setPageHeroImage,
  getPageHeroPosters,
  setPageHeroPoster,
  getPageHeroPositions,
  setPageHeroPosition,
  HERO_DISABLED_SENTINEL,
  HERO_PAGE_KEYS,
  type HeroPageKey,
  HERO_POSITIONS,
  type HeroPosition,
} from "@/utils/pageHeroImages.functions";
import {
  getPageHeroTexts,
  setPageHeroText,
  collectionHeroTextKey,
  type PageHeroText,
} from "@/utils/pageHeroTexts.functions";

type TabKey = "page" | "backgrounds" | "hero" | "texts";

export const Route = createFileRoute("/admin/page-backgrounds")({
  validateSearch: (search: Record<string, unknown>): { tab?: TabKey } => {
    const t = search.tab;
    return {
      tab:
        t === "hero" || t === "backgrounds" || t === "texts" || t === "page"
          ? t
          : undefined,
    };
  },
  component: AdminPageBackgroundsPage,
  head: () => ({
    meta: [
      { title: "Page Backgrounds · Lecmo Admin" },
      { name: "robots", content: "noindex" },
    ],
  }),
});

const BUCKET = "public-media";
const MAX_SIZE = 40 * 1024 * 1024;

const PAGE_LABELS: Record<PageKey, string> = {
  home: "الرئيسيّة (/)",
  "new-product": "منتج جديد (/new-product)",
  shop: "المتجر (/shop + /collection)",
  product: "صفحات المنتجات (/product/*)",
  about: "عنّا (/about)",
  journal: "اليوميّات (/journal)",
  contact: "تواصل (/contact)",
  gifts: "الإهداء (/gifts)",
  wishlist: "المفضّلة (/account/wishlist)",
  account: "صفحات الحساب (/account)",
  "collection-large": "المجموعات العطريّة الكبيرة (/collection/gift-set-perfume-collection)",
  "collection-ladybag": "حقيبة العطور النسائيّة (/collection/ladybag-collection)",
  ambassadors: "برنامج السفراء (/ambassadors)",
};

/** اسم الصفحة. */
function pageLabel(k: string): string {
  if (k in PAGE_LABELS) return PAGE_LABELS[k as PageKey];
  return k;
}

/** قائمة الصفحات + إدراج نطاق المنتج الحاليّ إن لم يكن ضمن الثوابت. */
function pageOptionsFor(current: string): string[] {
  return (PAGE_KEYS as readonly string[]).includes(current)
    ? [...PAGE_KEYS]
    : [current, ...PAGE_KEYS];
}


const DEVICE_LABELS: Record<DeviceKey, string> = {
  all: "كلّ الأجهزة",
  mobile: "موبايل فقط",
  desktop: "ديكسكتوب فقط",
};

const MARKET_LABELS: Record<MarketKey, string> = {
  all: "كلّ الأسواق",
  SA: "🇸🇦 السعوديّة",
  AE: "🇦🇪 الإمارات",
  IQ: "🇮🇶 العراق",
  OM: "🇴🇲 عُمان",
  US: "🇺🇸 الولايات المتحدة",
  KW: "🇰🇼 الكويت",
  QA: "🇶🇦 قطر",
  BH: "🇧🇭 البحرين",
  ROW: "🌍 بقيّة العالم",
};

/** نظرة عامّة: كلّ صفحة أمامها خلفيّتها الحاليّة + عدد الـvariants + تعديل مباشر */
function BackgroundsOverviewPanel({
  map,
  activeVariantKey,
  marketLabel,
  onPick,
}: {
  map: Record<string, PageVariants> | undefined;
  activeVariantKey: string;
  marketLabel?: string;
  onPick: (page: PageKey, device: DeviceKey, market: MarketKey) => void;
}) {

  const [query, setQuery] = React.useState("");
  const [onlySet, setOnlySet] = React.useState(false);

  const hasBg = React.useCallback(
    (k: string) => Object.values(map?.[k] ?? {}).some((v) => v?.url),
    [map],
  );

  const groups: { id: string; title: string; keys: string[] }[] = React.useMemo(() => {
    const main = (PAGE_KEYS as readonly string[]).filter((k) =>
      ["home", "shop", "product"].includes(k),
    );
    const rest = (PAGE_KEYS as readonly string[]).filter((k) => !main.includes(k));
    const q = query.trim().toLowerCase();
    const filter = (keys: string[]) =>
      keys.filter(
        (k) =>
          (!onlySet || hasBg(k)) &&
          (!q || k.toLowerCase().includes(q) || pageLabel(k).toLowerCase().includes(q)),
      );
    return [
      { id: "main", title: "الصفحات الأساسيّة", keys: filter(main) },
      { id: "rest", title: "بقيّة الصفحات", keys: filter(rest) },
    ].filter((g) => g.keys.length > 0);
  }, [query, onlySet, hasBg]);

  const renderRow = (k: string) => {
    const variants = map?.[k] ?? {};
    const entries = Object.entries(variants).filter(([, v]) => v?.url) as [
      string,
      PageBgSettings,
    ][];
    const picked =
      entries.find(([vk]) => vk === activeVariantKey) ??
      entries.find(([vk]) => vk === "all:all") ??
      entries[0] ??
      null;
    const [pdev, pmkt] = (picked?.[0] ?? "all:all").split(":") as [DeviceKey, MarketKey];
    const bg = picked?.[1];
    return (
      <div
        key={k}
        className="flex items-center gap-3 px-3 py-2.5"
      >
        <div className="h-12 w-20 shrink-0 overflow-hidden border border-border bg-muted">
          {bg?.url ? (
            <img src={bg.url} alt="" className="h-full w-full object-cover" loading="lazy" />
          ) : (
            <div className="flex h-full w-full items-center justify-center text-[10px] text-muted-foreground">
              —
            </div>
          )}
        </div>
        <div className="min-w-0 flex-1">
          <span className="block truncate text-xs font-medium">{pageLabel(k)}</span>
          <div className="mt-0.5 flex flex-wrap items-center gap-1.5 text-[11px] text-muted-foreground">
            {bg ? (
              <>
                <span className="border border-border px-1">
                  {pdev === "mobile" ? "📱 موبايل" : pdev === "desktop" ? "🖥 ديسكتوب" : "🖥📱 الكلّ"}
                </span>
                <span className="border border-border px-1">{MARKET_LABELS[pmkt] ?? pmkt}</span>
                <span className="border border-border px-1">{bg.size ?? "cover"}</span>
                <span>{entries.length} variant</span>
              </>
            ) : (
              <span>لا خلفيّة بعد</span>
            )}
          </div>
        </div>
        <Button
          size="sm"
          variant="outline"
          className="h-8"
          onClick={() => onPick(k as PageKey, bg ? pdev : "all", bg ? pmkt : "all")}
        >
          {bg ? "تعديل" : "إضافة"}
        </Button>
      </div>
    );
  };

  return (
    <div className="mb-4 rounded-none border border-border">
      <div className="border-b border-border bg-muted/40 px-3 py-2 text-xs font-medium">
        نظرة عامّة — كلّ الصفحات وخلفيّاتها{marketLabel ? ` (${marketLabel})` : ""}
      </div>

      <div className="flex flex-wrap items-center gap-2 border-b border-border px-3 py-2">
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="ابحث عن صفحة…"
          className="h-8 min-w-[10rem] flex-1 border border-border bg-background px-2 text-xs"
        />
        <Button
          type="button"
          size="sm"
          variant={onlySet ? "default" : "outline"}
          className="h-8 text-[11px]"
          onClick={() => setOnlySet((v) => !v)}
        >
          {onlySet ? "عرض الكلّ" : "التي لها خلفيّة فقط"}
        </Button>
      </div>

      {groups.length === 0 ? (
        <div className="px-3 py-6 text-center text-xs text-muted-foreground">لا نتائج مطابقة.</div>
      ) : (
        groups.map((g) => (
          <details key={g.id} open className="border-b border-border last:border-b-0">
            <summary className="cursor-pointer bg-muted/20 px-3 py-1.5 text-[11px] uppercase tracking-wider text-muted-foreground">
              {g.title} ({g.keys.length})
            </summary>
            <div className="divide-y divide-border">{g.keys.map(renderRow)}</div>
          </details>
        ))
      )}
    </div>
  );
}



function AdminPageBackgroundsPage() {
  const { ready } = useAdminGate();
  const qc = useQueryClient();
  const getFn = useServerFn(getPageBackgrounds);
  const setFn = useServerFn(setPageBackground);
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const tab: TabKey = search.tab ?? "page";

  const { data, isLoading } = useQuery({
    queryKey: ["page-backgrounds"],
    queryFn: () => getFn(),
    enabled: ready,
  });

  const [pageKey, setPageKey] = React.useState<PageKey>("home");
  const [device, setDevice] = React.useState<DeviceKey>("all");
  const [market, setMarket] = React.useState<MarketKey>("all");
  const [draftUrl, setDraftUrl] = React.useState<string>("");
  const [draftSize, setDraftSize] = React.useState<PageBgSize>("cover");
  const [draftPosX, setDraftPosX] = React.useState<number>(50);
  const [draftPosY, setDraftPosY] = React.useState<number>(50);
  const [draftMode, setDraftMode] = React.useState<PageBgMode>("fixed");
  const [draftColor, setDraftColor] = React.useState<string>("");
  const [uploading, setUploading] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [deleting, setDeleting] = React.useState(false);
  const [isDragging, setIsDragging] = React.useState(false);
  const fileRef = React.useRef<HTMLInputElement>(null);
  const editorRef = React.useRef<HTMLDivElement>(null);
  const [highlightEditor, setHighlightEditor] = React.useState(false);


  const variantKey = makeVariantKey(device, market);

  const onDragOver = React.useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isDragging) setIsDragging(true);
  }, [isDragging]);
  const onDragLeave = React.useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);
  const onDrop = React.useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    const f = e.dataTransfer.files?.[0];
    if (f) handleUpload(f);
  }, []);

  // منع المتصفّح من فتح الملف إذا أُفلت خارج منطقة الرفع
  React.useEffect(() => {
    const onDocDragOver = (e: DragEvent) => {
      e.preventDefault();
    };
    const onDocDrop = (e: DragEvent) => {
      // إذا كان الإفلات على منطقة أخرى غير منطقة الرفع، نمنع السلوك الافتراضي
      if (!e.target || !(e.target as HTMLElement).closest("[data-drop-zone]")) {
        e.preventDefault();
      }
    };
    document.addEventListener("dragover", onDocDragOver);
    document.addEventListener("drop", onDocDrop);
    return () => {
      document.removeEventListener("dragover", onDocDragOver);
      document.removeEventListener("drop", onDocDrop);
    };
  }, []);

  // كلّما تغيّر pageKey/device/market أو وصلت البيانات، حمِّل إعدادات الـvariant الحاليّ
  React.useEffect(() => {
    if (!data) return;
    const cur = data.map[pageKey]?.[variantKey];
    setDraftUrl(cur?.url ?? "");
    setDraftSize(cur?.size ?? "cover");
    setDraftPosX(cur?.posX ?? 50);
    setDraftPosY(cur?.posY ?? 50);
    setDraftMode(cur?.mode ?? "fixed");
    setDraftColor(cur?.color ?? "");
  }, [data, pageKey, variantKey]);

  const current = data?.map[pageKey]?.[variantKey];
  const isDirty =
    (current?.url ?? "") !== draftUrl ||
    (current?.size ?? "cover") !== draftSize ||
    (current?.posX ?? 50) !== draftPosX ||
    (current?.posY ?? 50) !== draftPosY ||
    (current?.mode ?? "fixed") !== draftMode ||
    (current?.color ?? "") !== draftColor;

  // كم variant مفعَّل لكلّ صفحة (لإظهار العدد في القائمة)
  const variantCounts = React.useMemo(() => {
    const out: Record<PageKey, number> = {} as Record<PageKey, number>;
    for (const k of PAGE_KEYS) {
      out[k] = Object.values(data?.map[k] ?? {}).filter((v) => v?.url || v?.color).length;
    }
    return out;
  }, [data]);

  async function handleUpload(file: File) {
    if (!file.type.startsWith("image/")) {
      toast.error("الملف يجب أن يكون صورة");
      return;
    }
    if (file.size > MAX_SIZE) {
      toast.error("الصورة كبيرة جداً (الحدّ ٤٠MB)");
      return;
    }
    setUploading(true);
    try {
      const compressed = await compressImageBeforeUpload(file, HERO_COMPRESS_OPTIONS).catch(() => file);
      const ext = (compressed.name.split(".").pop() ?? "jpg").toLowerCase();
      const path = `page-bg/${pageKey}/${variantKey.replace(":", "-")}/${Date.now()}.${ext}`;
      const { error } = await supabase.storage
        .from(BUCKET)
        .upload(path, compressed, { upsert: false, cacheControl: "3600" });
      if (error) throw error;
      const { data: pub } = supabase.storage.from(BUCKET).getPublicUrl(path);
      setDraftUrl(bustUrl(pub.publicUrl));
      toast.success("تمّ الرفع — اضغط حفظ لتفعيل التغيير");
    } catch (e) {
      toast.error("فشل الرفع: " + (e instanceof Error ? e.message : String(e)));
    } finally {
      setUploading(false);
    }
  }

  async function handleSave() {
    setSaving(true);
    try {
      await setFn({
        data: {
          pageKey,
          device,
          market,
          url: draftUrl,
          size: draftSize,
          posX: draftPosX,
          posY: draftPosY,
          mode: draftMode,
          color: draftColor,
        },
      });
      await qc.invalidateQueries({ queryKey: ["page-backgrounds"] });
      toast.success("تمّ الحفظ");
    } catch (e) {
      toast.error("فشل الحفظ: " + (e instanceof Error ? e.message : String(e)));
    } finally {
      setSaving(false);
    }
  }

  function handleClear() {
    setDraftUrl("");
  }

  async function handleDeleteVariant() {
    if (!current?.url) {
      // لا شيء محفوظ على السيرفر — اكتفِ بمسح المسوّدة
      setDraftUrl("");
      setDraftSize("cover");
      setDraftPosX(50);
      setDraftPosY(50);
      setDraftMode("fixed");
      return;
    }
    const ok = window.confirm(
      `سيتمّ حذف خلفيّة "${pageLabel(pageKey)}" للـvariant: ${DEVICE_LABELS[device]} · ${MARKET_LABELS[market]} نهائياً.\n\nباقي الـvariants لن تتأثّر. متابعة؟`,
    );
    if (!ok) return;
    setDeleting(true);
    try {
      await setFn({
        data: { pageKey, device, market, url: "" },
      });
      await qc.invalidateQueries({ queryKey: ["page-backgrounds"] });
      setDraftUrl("");
      setDraftSize("cover");
      setDraftPosX(50);
      setDraftPosY(50);
      setDraftMode("fixed");
      toast.success("تمّ حذف هذا الـvariant");
    } catch (e) {
      toast.error("فشل الحذف: " + (e instanceof Error ? e.message : String(e)));
    } finally {
      setDeleting(false);
    }
  }

  function resetCrop() {
    setDraftSize("cover");
    setDraftPosX(50);
    setDraftPosY(50);
  }

  if (!ready || isLoading) {
    return (
      <SiteShell>
        <div className="mx-auto max-w-3xl px-4 py-10">
          <AdminNav />
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> جاري التحميل…
          </div>
        </div>
      </SiteShell>
    );
  }

  return (
    <SiteShell>
      <div className="px-4 py-8" dir="rtl">
        <AdminNav />
        <Tabs
          value={tab}
          onValueChange={(v) =>
            navigate({
              search: { tab: v === "page" ? undefined : (v as TabKey) },
              replace: true,
            })
          }
          className="mx-auto w-full max-w-[1600px]"
        >
          <TabsList className="mb-6 flex w-full flex-wrap justify-start gap-1">
            <TabsTrigger value="page">🧩 عناصر الصفحة (الكلّ في مكان)</TabsTrigger>
            <TabsTrigger value="backgrounds">🖼️ خلفيّات الصفحات</TabsTrigger>
            <TabsTrigger value="hero">🎬 حملات/صور الهيرو (بانر الهوم)</TabsTrigger>
            <TabsTrigger value="texts">✍️ نصوص الهيرو</TabsTrigger>
          </TabsList>

          <TabsContent value="page">
            <PageElementsCenter />
          </TabsContent>

          <TabsContent value="backgrounds" className="mx-auto max-w-3xl">
        <div className="mb-6 flex items-center justify-between gap-3">

          <div>
            <h1 className="t-h2 text-foreground">خلفيّات الصفحات</h1>
            <p className="mt-1 text-sm text-muted-foreground">
              اختر الصفحة + الجهاز + السوق، ثمّ ارفع صورة. ترتيب الأولويّة عند العرض:
              جهاز+سوق → جهاز+الكلّ → الكلّ+سوق → الكلّ+الكلّ.
            </p>
          </div>
          <Link
            to="/admin"
            className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="h-4 w-4" /> رجوع
          </Link>
        </div>

        {/* تبويبات الأسواق — بنفس نمط لوحة الهيرو */}
        <div className="mb-3 flex w-full flex-wrap justify-start gap-1">
          {MARKET_KEYS.map((m) => (
            <button
              key={m}
              type="button"
              onClick={() => setMarket(m)}
              className={`min-h-[36px] border px-2.5 py-1.5 text-xs transition ${
                market === m
                  ? "border-foreground bg-foreground text-background"
                  : "border-border text-muted-foreground hover:text-foreground"
              }`}
            >
              {MARKET_LABELS[m]}
            </button>
          ))}
        </div>

        {/* نظرة عامّة — كلّ الصفحات وخلفيّاتها الحاليّة */}
        <BackgroundsOverviewPanel
          map={data?.map}
          activeVariantKey={variantKey}
          marketLabel={MARKET_LABELS[market]}

          onPick={(p, d, m) => {
            setPageKey(p);
            setDevice(d);
            setMarket(m);
            setHighlightEditor(true);
            window.setTimeout(() => {
              editorRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
            }, 50);
            window.setTimeout(() => setHighlightEditor(false), 1600);
          }}
        />

        {/* اختيار الصفحة + الجهاز + السوق — في الأعلى لتكون واضحة مباشرة */}
        <div
          ref={editorRef}
          className={`mb-4 grid gap-3 rounded-none border bg-card p-4 transition sm:grid-cols-3 ${
            highlightEditor ? "border-primary ring-2 ring-primary/40" : "border-border"
          }`}
        >
          <div className="space-y-1.5">

            <label className="text-xs font-medium text-foreground">الصفحة</label>
            <Select value={pageKey} onValueChange={(v) => setPageKey(v as PageKey)} dir="rtl">
              <SelectTrigger className="h-9 text-sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent position="popper" sideOffset={4} align="start">
                {pageOptionsFor(pageKey).map((k) => (
                  <SelectItem key={k} value={k}>
                    {pageLabel(k)}
                    {(variantCounts[k as PageKey] ?? 0) > 0 ? `  •  ${variantCounts[k as PageKey]}` : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <label className="text-xs font-medium text-foreground">الجهاز</label>
            <Select value={device} onValueChange={(v) => setDevice(v as DeviceKey)} dir="rtl">
              <SelectTrigger className="h-9 text-sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent position="popper" sideOffset={4} align="start">
                {DEVICE_KEYS.map((d) => (
                  <SelectItem key={d} value={d}>
                    {DEVICE_LABELS[d]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <label className="text-xs font-medium text-foreground">السوق</label>
            <Select value={market} onValueChange={(v) => setMarket(v as MarketKey)} dir="rtl">
              <SelectTrigger className="h-9 text-sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent position="popper" sideOffset={4} align="start">
                {MARKET_KEYS.map((m) => (
                  <SelectItem key={m} value={m}>
                    {MARKET_LABELS[m]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="space-y-5 rounded-none border border-border bg-card p-5">
          <div data-drop-zone>
            <p className="mb-2 text-sm font-medium text-foreground">
              المعاينة — {pageLabel(pageKey)} · {DEVICE_LABELS[device]} · {MARKET_LABELS[market]}
            </p>
            {draftUrl ? (
              <div
                className={`relative aspect-[16/10] w-full overflow-hidden rounded-none border bg-muted transition ${
                  isDragging ? "border-primary ring-2 ring-primary/40" : "border-border"
                }`}
                style={{
                  backgroundImage: `url('${draftUrl}')`,
                  backgroundSize: draftSize,
                  backgroundPosition: `${draftPosX}% ${draftPosY}%`,
                  backgroundRepeat: "no-repeat",
                }}
                onDragOver={onDragOver}
                onDragLeave={onDragLeave}
                onDrop={onDrop}
              >
                {isDragging && (
                  <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-background/80 text-sm font-medium text-foreground">
                    <Upload className="h-8 w-8 animate-bounce" />
                    أفلت الصورة هنا لاستبدالها
                  </div>
                )}
              </div>
            ) : (
              <button
                type="button"
                onClick={() => fileRef.current?.click()}
                onDragOver={onDragOver}
                onDragLeave={onDragLeave}
                onDrop={onDrop}
                style={draftColor ? { backgroundColor: draftColor } : undefined}
                className={`group flex aspect-[16/10] w-full flex-col items-center justify-center gap-3 rounded-none border-2 border-dashed bg-muted text-sm transition-all ${
                  isDragging
                    ? "scale-[1.01] border-primary bg-primary/10 text-foreground"
                    : "border-border text-muted-foreground hover:border-foreground/40 hover:bg-muted/80 hover:text-foreground"
                }`}
              >
                {uploading ? (
                  <Loader2 className="h-8 w-8 animate-spin" />
                ) : (
                  <div className="rounded-full bg-background p-3  transition group-hover:scale-105">
                    <Upload className="h-6 w-6" />
                  </div>
                )}
                <span className="font-medium">
                  {isDragging
                    ? "أفلت الصورة هنا"
                    : "اسحب الصورة إلى هنا أو انقر للاختيار"}
                </span>
                <span className="text-[11px] text-muted-foreground">
                  سيتمّ الضغط تلقائيّاً قبل الرفع — JPG / PNG / WebP (الحدّ ٤٠MB)
                </span>
              </button>
            )}
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) handleUpload(f);
                e.target.value = "";
              }}
            />
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={uploading}
              onClick={() => fileRef.current?.click()}
            >
              {uploading ? (
                <Loader2 className="me-2 h-4 w-4 animate-spin" />
              ) : (
                <Upload className="me-2 h-4 w-4" />
              )}
              رفع صورة جديدة
            </Button>
            {draftUrl && draftUrl !== (current?.url ?? "") && (
              <Button type="button" variant="outline" size="sm" onClick={handleClear}>
                <Trash2 className="me-2 h-4 w-4" /> تجاهل الصورة الجديدة
              </Button>
            )}
            <Button type="button" variant="ghost" size="sm" onClick={resetCrop}>
              إعادة ضبط الكروب
            </Button>
            <Button
              type="button"
              variant="destructive"
              size="sm"
              className="ms-auto"
              disabled={deleting || (!current?.url && !draftUrl)}
              onClick={handleDeleteVariant}
              title={`حذف خلفيّة هذا الـvariant فقط: ${DEVICE_LABELS[device]} · ${MARKET_LABELS[market]}`}
            >
              {deleting ? (
                <Loader2 className="me-2 h-4 w-4 animate-spin" />
              ) : (
                <Trash2 className="me-2 h-4 w-4" />
              )}
              حذف هذا الـvariant نهائياً
            </Button>
          </div>

          <p className="text-[11px] text-muted-foreground">
            «حذف هذا الـvariant» يمسح فقط الصورة الخاصّة بـ{" "}
            <span className="font-medium text-foreground">
              {DEVICE_LABELS[device]} · {MARKET_LABELS[market]}
            </span>{" "}
            من صفحة <span className="font-medium text-foreground">{pageLabel(pageKey)}</span> —
            باقي variants لن تتأثّر.
          </p>


          <div className="space-y-2 rounded-none border border-border/60 bg-background/40 p-4">
            <label className="text-xs font-medium text-foreground">لون الخلفيّة (اختياريّ)</label>
            <p className="text-[11px] text-muted-foreground">
              يُستخدم وحده إن لم ترفع صورة، أو كلون خلفيّ تحت الصورة عند وجود فراغات (Contain / Auto).
            </p>
            <div className="flex flex-wrap items-center gap-2">
              <input
                type="color"
                value={/^#[0-9a-fA-F]{6}$/.test(draftColor) ? draftColor : "#000000"}
                onChange={(e) => setDraftColor(e.target.value)}
                className="h-9 w-14 cursor-pointer rounded-none border border-border bg-background p-1"
                aria-label="لون الخلفيّة"
              />
              <input
                type="text"
                value={draftColor}
                onChange={(e) => setDraftColor(e.target.value.trim())}
                placeholder="#101010"
                dir="ltr"
                lang="en"
                className="h-9 w-32 rounded-none border border-border bg-background px-2 text-sm"
              />
              {draftColor && (
                <Button type="button" variant="ghost" size="sm" onClick={() => setDraftColor("")}>
                  إزالة اللون
                </Button>
              )}
            </div>
          </div>

          {draftUrl && (
            <div className="space-y-4 rounded-none border border-border/60 bg-background/40 p-4">
              <div className="space-y-1.5">
                <label className="text-xs font-medium text-foreground">حجم الصورة</label>
                <Select value={draftSize} onValueChange={(v) => setDraftSize(v as PageBgSize)} dir="rtl">
                  <SelectTrigger className="h-9 text-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent position="popper" sideOffset={4} align="start">
                    <SelectItem value="cover">Cover — تملأ الشاشة كاملةً (قد تُقصّ)</SelectItem>
                    <SelectItem value="contain">Contain — تظهر الصورة كاملةً (قد تترك فراغاً)</SelectItem>
                    <SelectItem value="100%">100% — بعرض الشاشة بالضبط</SelectItem>
                    <SelectItem value="auto">Auto — حجمها الأصليّ</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1.5">
                <label className="text-xs font-medium text-foreground">وضع العرض</label>
                <Select value={draftMode} onValueChange={(v) => setDraftMode(v as PageBgMode)} dir="rtl">
                  <SelectTrigger className="h-9 text-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent position="popper" sideOffset={4} align="start">
                    <SelectItem value="fixed">ثابتة (افتراضي) — تملأ الشاشة وتبقى أثناء التمرير</SelectItem>
                    <SelectItem value="parallax_tall">ستوريَين (شاشتان) + parallax — صورة طويلة تتحرّك ببطء</SelectItem>
                  </SelectContent>
                </Select>
                {draftMode === "parallax_tall" && (
                  <p className="text-[11px] text-muted-foreground">
                    الصورة تُعرض بحجم شاشتين (200vh) وتتحرّك بنصف سرعة التمرير — يُفضَّل صورة طوليّة (نسبة 9:32 تقريباً).
                  </p>
                )}
              </div>




              <div className="space-y-1.5">
                <div className="flex items-center justify-between">
                  <label className="text-xs font-medium text-foreground">الموضع الأفقيّ (X)</label>
                  <span className="text-xs tabular-nums text-muted-foreground">{draftPosX}%</span>
                </div>
                <Slider
                  min={0}
                  max={100}
                  step={1}
                  value={[draftPosX]}
                  onValueChange={(v) => setDraftPosX(v[0] ?? 50)}
                  onValueCommit={(v) => setDraftPosX(v[0] ?? 50)}
                />
                <p className="text-[11px] text-muted-foreground">0% = يسار · 50% = منتصف · 100% = يمين</p>
              </div>

              <div className="space-y-1.5">
                <div className="flex items-center justify-between">
                  <label className="text-xs font-medium text-foreground">الموضع العموديّ (Y)</label>
                  <span className="text-xs tabular-nums text-muted-foreground">{draftPosY}%</span>
                </div>
                <Slider
                  min={0}
                  max={100}
                  step={1}
                  value={[draftPosY]}
                  onValueChange={(v) => setDraftPosY(v[0] ?? 50)}
                  onValueCommit={(v) => setDraftPosY(v[0] ?? 50)}
                />
                <p className="text-[11px] text-muted-foreground">0% = أعلى · 50% = منتصف · 100% = أسفل</p>
              </div>
            </div>
          )}

          <div className="flex items-center justify-between gap-3 border-t border-border pt-4">
            <div className="text-xs">
              {isDirty ? (
                <span className="font-medium text-foreground">لديك تغييرات غير محفوظة — اضغط حفظ لتطبيق آخر تعديل.</span>
              ) : (
                <span className="text-muted-foreground">لا توجد تغييرات غير محفوظة حالياً.</span>
              )}
            </div>
            <Button
              type="button"
              size="sm"
              variant={isDirty ? "default" : "secondary"}
              className="min-w-24"
              disabled={!isDirty || saving}
              onClick={handleSave}
            >
              {saving && <Loader2 className="me-2 h-4 w-4 animate-spin" />}
              حفظ
            </Button>
          </div>
        </div>

        {/* زرّ النزول السفليّ في الهيرو لكلّ صفحة */}
        <PageHeroCtasPanel />

        {/* قسم صور الهيرو العلويّ — للصفحات: shop / about / journal / contact */}
        <PageHeroImagesPanel />

        {/* سجلّ الخلفيّات — صور مصغّرة + أزرار تعديل/حذف لكلّ variant */}
        <PageBackgroundsRegistry
          map={data?.map ?? {}}
          variantCounts={variantCounts}
          currentPageKey={pageKey}
          onSelect={(p, d, m) => {
            setPageKey(p);
            setDevice(d);
            setMarket(m);
            window.scrollTo({ top: 0, behavior: "smooth" });
          }}
          onDeleteVariant={async (p, d, m) => {
            const ok = window.confirm(
              `حذف خلفيّة "${pageLabel(p)}" — ${DEVICE_LABELS[d]} · ${MARKET_LABELS[m]} نهائياً؟`,
            );
            if (!ok) return;
            try {
              await setFn({ data: { pageKey: p, device: d, market: m, url: "" } });
              await qc.invalidateQueries({ queryKey: ["page-backgrounds"] });
              toast.success("تمّ الحذف");
            } catch (e) {
              toast.error("فشل الحذف: " + (e instanceof Error ? e.message : String(e)));
            }
          }}
          onDeleteAllForPage={async (p) => {
            const variants = Object.entries(data?.map[p] ?? {}).filter(([, v]) => v?.url);
            if (variants.length === 0) return;
            const ok = window.confirm(
              `حذف كلّ خلفيّات صفحة "${pageLabel(p)}" (${variants.length}) نهائياً؟`,
            );
            if (!ok) return;
            try {
              for (const [vk] of variants) {
                const [d, m] = vk.split(":") as [DeviceKey, MarketKey];
                await setFn({ data: { pageKey: p, device: d, market: m, url: "" } });
              }
              await qc.invalidateQueries({ queryKey: ["page-backgrounds"] });
              toast.success("تمّ حذف كلّ خلفيّات الصفحة");
            } catch (e) {
              toast.error("فشل الحذف: " + (e instanceof Error ? e.message : String(e)));
            }
          }}
        />
          </TabsContent>

          <TabsContent value="hero">
            <div className="mb-4 border border-border bg-muted/40 px-4 py-3 text-sm text-muted-foreground">
              من هنا تُعدّل صور/فيديو بانر الهوم (الهيرو). الحملة المفعّلة لكل سوق تتقدّم على صورة الهيرو الافتراضيّة.
            </div>
            <HeroMediaSection />
          </TabsContent>

          <TabsContent value="texts">
            <PageHeroTextsPanel />
          </TabsContent>
        </Tabs>
      </div>
    </SiteShell>

  );
}

function PageBackgroundsRegistry({
  map,
  variantCounts,
  currentPageKey,
  onSelect,
  onDeleteVariant,
  onDeleteAllForPage,
}: {
  map: Partial<Record<PageKey, PageVariants>>;
  variantCounts: Record<PageKey, number>;
  currentPageKey: PageKey;
  onSelect: (p: PageKey, d: DeviceKey, m: MarketKey) => void;
  onDeleteVariant: (p: PageKey, d: DeviceKey, m: MarketKey) => void | Promise<void>;
  onDeleteAllForPage: (p: PageKey) => void | Promise<void>;
}) {
  const [hotspotsFor, setHotspotsFor] = React.useState<
    { pk: PageKey; vk: string; url: string; label: string } | null
  >(null);
  const activePages = PAGE_KEYS.filter((k) => variantCounts[k] > 0).length;
  return (
    <div className="mb-4 rounded-none border border-border bg-card p-4">
      <div className="mb-3 flex items-center justify-between">
        <h2 className="text-sm font-semibold text-foreground">سجلّ الخلفيّات المفعَّلة</h2>
        <span className="text-[11px] text-muted-foreground">
          {activePages} / {PAGE_KEYS.length} صفحات مفعَّلة
        </span>
      </div>

      <div className="space-y-3">
        {PAGE_KEYS.map((k) => {
          const variants = Object.entries(map[k] ?? {}).filter(
            (entry): entry is [string, PageBgSettings] => Boolean(entry[1]?.url),
          );
          const isSelected = currentPageKey === k;
          return (
            <div
              key={k}
              className={`rounded-none border p-3 transition ${
                isSelected ? "border-primary bg-primary/5" : "border-border bg-background"
              }`}
            >
              <div className="mb-2 flex items-center justify-between gap-2">
                <div className="flex items-center gap-2 text-sm">
                  <span
                    className={`h-2 w-2 rounded-full ${
                      variants.length > 0 ? "bg-emerald-500" : "bg-muted-foreground/40"
                    }`}
                  />
                  <span className="font-medium text-foreground">{pageLabel(k)}</span>
                  <span className="text-[11px] text-muted-foreground">
                    {variants.length > 0 ? `${variants.length} صورة` : "فارغة"}
                  </span>
                </div>
                {variants.length > 0 && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-7 px-2 text-xs text-destructive hover:bg-destructive/10"
                    onClick={() => void onDeleteAllForPage(k)}
                  >
                    <Trash2 className="me-1 h-3.5 w-3.5" /> حذف الكلّ
                  </Button>
                )}
              </div>

              {variants.length > 0 ? (
                <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 md:grid-cols-4">
                  {variants.map(([vk, v]) => {
                    const [d, m] = vk.split(":") as [DeviceKey, MarketKey];
                    return (
                      <div
                        key={vk}
                        className="group relative overflow-hidden rounded-none border border-border bg-muted"
                      >
                        <div
                          className="aspect-[16/10] w-full"
                          style={{
                            backgroundImage: `url('${v.url}')`,
                            backgroundSize: v.size,
                            backgroundPosition: `${v.posX}% ${v.posY}%`,
                            backgroundRepeat: "no-repeat",
                          }}
                        />
                        <div className="flex items-center justify-between gap-1 border-t border-border bg-background/95 px-1.5 py-1">
                          <span className="truncate text-[10px] text-muted-foreground">
                            {DEVICE_LABELS[d].replace(" فقط", "")} · {MARKET_LABELS[m]}
                          </span>
                          <div className="flex shrink-0 items-center gap-0.5">
                            <button
                              type="button"
                              onClick={() => onSelect(k, d, m)}
                              className="rounded px-1.5 py-0.5 text-[10px] text-foreground hover:bg-muted"
                              title="تعديل / تبديل"
                            >
                              تعديل
                            </button>
                            <button
                              type="button"
                              onClick={() =>
                                setHotspotsFor({
                                  pk: k,
                                  vk,
                                  url: v.url,
                                  label: `${pageLabel(k)} · ${DEVICE_LABELS[d]} · ${MARKET_LABELS[m]}`,
                                })
                              }
                              className="rounded p-1 text-foreground hover:bg-muted"
                              title="نقاط تفاعليّة"
                            >
                              <MapPin className="h-3 w-3" />
                            </button>
                            <button
                              type="button"
                              onClick={() => void onDeleteVariant(k, d, m)}
                              className="rounded p-1 text-destructive hover:bg-destructive/10"
                              title="حذف هذا الـvariant"
                            >
                              <Trash2 className="h-3 w-3" />
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => onSelect(k, "all", "all")}
                  className="w-full rounded-none border border-dashed border-border bg-muted/30 px-3 py-3 text-xs text-muted-foreground hover:border-foreground/40 hover:text-foreground"
                >
                  لا توجد خلفيّة بعد — اضغط للإضافة
                </button>
              )}
            </div>
          );
        })}
      </div>
      {hotspotsFor && (
        <HotspotsEditorDialog
          open={!!hotspotsFor}
          onOpenChange={(o) => !o && setHotspotsFor(null)}
          scope="page_background"
          scopeId={`${hotspotsFor.pk}:${hotspotsFor.vk}`}
          previewImageUrl={hotspotsFor.url}
          title={`نقاط خلفيّة: ${hotspotsFor.label}`}
        />
      )}
    </div>
  );
}

// ────────────────────────────────────────────────────────────
// قسم صور الهيرو العلويّ (PageHero) — منفصل عن خلفيّة الصفحة
// الصفحات المدعومة فعليّاً (التي تستعمل <PageHero pageKey=...>):
const HERO_PAGE_LABELS: Record<HeroPageKey, string> = {
  shop: "المتجر (/shop)",
  about: "عنّا (/about)",
  journal: "اليوميّات (/journal)",
  contact: "تواصل (/contact)",
  ambassadors: "السفراء (/ambassadors)",
  "ambassadors-tier": "شرائح السفراء (/ambassadors/tier/*)",
  gifts: "الإهداء (/gifts)",
  "collection-large": "مجموعة: العطور الكبيرة (/collection/*)",
  "collection-ladybag": "مجموعة: حقيبة السيّدة (/collection/*)",
  cart: "السلّة (بانر صغير أعلى السلّة)",
  product: "صفحة المنتج (/product/*) — بانر أعلى الصفحة",
  account: "حسابي (/account)",
  wishlist: "المفضّلة (/account/wishlist)",
};


function PageHeroImagesPanel() {
  const [hotspotsFor, setHotspotsFor] = React.useState<{ pk: HeroPageKey; url: string | undefined } | null>(null);
  const qc = useQueryClient();
  const getFn = useServerFn(getPageHeroImages);
  const setFn = useServerFn(setPageHeroImage);
  const getPositionsFn = useServerFn(getPageHeroPositions);
  const getPostersFn = useServerFn(getPageHeroPosters);
  const setPosterFn = useServerFn(setPageHeroPoster);
  const setPositionFn = useServerFn(setPageHeroPosition);
  const { ready } = useAdminGate();

  const { data, isLoading } = useQuery({
    queryKey: ["page-hero-images"],
    queryFn: () => getFn(),
    enabled: ready,
  });

  const { data: positionsData } = useQuery({
    queryKey: ["page-hero-positions"],
    queryFn: () => getPositionsFn(),
    enabled: ready,
  });

  const { data: postersData } = useQuery({
    queryKey: ["page-hero-posters"],
    queryFn: () => getPostersFn(),
    enabled: ready,
  });

  const [busyKey, setBusyKey] = React.useState<HeroPageKey | null>(null);
  const fileRefs = React.useRef<Record<HeroPageKey, HTMLInputElement | null>>({
    shop: null,
    about: null,
    journal: null,
    contact: null,
    ambassadors: null,
    "ambassadors-tier": null,
    gifts: null,
    "collection-large": null,
    "collection-ladybag": null,
    cart: null,
    product: null,
    account: null,
    wishlist: null,
  });

  async function handleSetPosition(pk: HeroPageKey, position: HeroPosition) {
    setBusyKey(pk);
    try {
      await setPositionFn({ data: { pageKey: pk, position } });
      await qc.invalidateQueries({ queryKey: ["page-hero-positions"] });
      toast.success("تمّ تغيير موضع النصّ");
    } catch (e) {
      toast.error("فشل التحديث: " + (e instanceof Error ? e.message : String(e)));
    } finally {
      setBusyKey(null);
    }
  }


  async function handleUpload(pk: HeroPageKey, file: File) {
    if (!file.type.startsWith("image/")) {
      toast.error("الملف يجب أن يكون صورة");
      return;
    }
    if (file.size > MAX_SIZE) {
      toast.error("الصورة كبيرة جداً (الحدّ ٤٠MB)");
      return;
    }
    setBusyKey(pk);
    try {
      const compressed = await compressImageBeforeUpload(file, HERO_COMPRESS_OPTIONS).catch(() => file);
      const ext = (compressed.name.split(".").pop() ?? "jpg").toLowerCase();
      const path = `page-hero/${pk}/${Date.now()}.${ext}`;
      const { error } = await supabase.storage
        .from(BUCKET)
        .upload(path, compressed, { upsert: false, cacheControl: "3600" });
      if (error) throw error;
      const { data: pub } = supabase.storage.from(BUCKET).getPublicUrl(path);
      await setFn({ data: { pageKey: pk, url: bustUrl(pub.publicUrl) } });
      await qc.invalidateQueries({ queryKey: ["page-hero-images"] });
      toast.success("تمّ رفع صورة الهيرو وتفعيلها");
    } catch (e) {
      toast.error("فشل الرفع: " + (e instanceof Error ? e.message : String(e)));
    } finally {
      setBusyKey(null);
    }
  }

  async function handlePosterUpload(pk: HeroPageKey, file: File) {
    if (!file.type.startsWith("image/")) {
      toast.error("غلاف الفيديو يجب أن يكون صورة");
      return;
    }
    if (file.size > MAX_SIZE) {
      toast.error("الصورة كبيرة جداً (الحدّ ٤٠MB)");
      return;
    }
    setBusyKey(pk);
    try {
      const compressed = await compressImageBeforeUpload(file, HERO_COMPRESS_OPTIONS).catch(() => file);
      const ext = (compressed.name.split(".").pop() ?? "jpg").toLowerCase();
      const path = `page-hero/${pk}/poster-${Date.now()}.${ext}`;
      const { error } = await supabase.storage
        .from(BUCKET)
        .upload(path, compressed, { upsert: false, cacheControl: "3600" });
      if (error) throw error;
      const { data: pub } = supabase.storage.from(BUCKET).getPublicUrl(path);
      await setPosterFn({ data: { pageKey: pk, url: bustUrl(pub.publicUrl) } });
      await qc.invalidateQueries({ queryKey: ["page-hero-posters"] });
      toast.success("تمّ رفع صورة غلاف الفيديو");
    } catch (e) {
      toast.error("فشل الرفع: " + (e instanceof Error ? e.message : String(e)));
    } finally {
      setBusyKey(null);
    }
  }

  async function handlePosterDelete(pk: HeroPageKey) {
    setBusyKey(pk);
    try {
      await setPosterFn({ data: { pageKey: pk, url: "" } });
      await qc.invalidateQueries({ queryKey: ["page-hero-posters"] });
      toast.success("تمّ حذف صورة الغلاف");
    } catch (e) {
      toast.error("فشل الحذف: " + (e instanceof Error ? e.message : String(e)));
    } finally {
      setBusyKey(null);
    }
  }

  async function handleDelete(pk: HeroPageKey) {
    const ok = window.confirm(`حذف صورة هيرو "${HERO_PAGE_LABELS[pk]}" والعودة للصورة الافتراضيّة؟`);
    if (!ok) return;
    setBusyKey(pk);
    try {
      await setFn({ data: { pageKey: pk, url: "" } });
      await qc.invalidateQueries({ queryKey: ["page-hero-images"] });
      toast.success("تمّ الحذف — رجعت الصورة الافتراضيّة");
    } catch (e) {
      toast.error("فشل الحذف: " + (e instanceof Error ? e.message : String(e)));
    } finally {
      setBusyKey(null);
    }
  }

  async function handleToggleDisabled(pk: HeroPageKey, currentlyDisabled: boolean) {
    setBusyKey(pk);
    try {
      await setFn({
        data: { pageKey: pk, url: currentlyDisabled ? "" : HERO_DISABLED_SENTINEL },
      });
      await qc.invalidateQueries({ queryKey: ["page-hero-images"] });
      toast.success(currentlyDisabled ? "تمّ تفعيل الهيرو" : "تمّ تعطيل الهيرو لهذه الصفحة");
    } catch (e) {
      toast.error("فشل التحديث: " + (e instanceof Error ? e.message : String(e)));
    } finally {
      setBusyKey(null);
    }
  }

  return (
    <div className="mb-4 rounded-none border border-border bg-card p-4">
      <div className="mb-3 flex items-center justify-between">
        <h2 className="text-sm font-semibold text-foreground">صور الهيرو العلويّ</h2>
        <span className="text-[11px] text-muted-foreground">
          البانر الكبير أعلى الصفحة (فوق المحتوى)
        </span>
      </div>
      <p className="mb-3 text-[11px] text-muted-foreground">
        إن وُجد فيديو هيرو من <span className="font-medium text-foreground">تاب «حملات الهيرو»</span> فسيظهر بدل الصورة.
        هذه الصورة fallback عند غياب الفيديو.
      </p>
      {isLoading ? (
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> جاري التحميل…
        </div>
      ) : (
        <div className="grid gap-2 sm:grid-cols-2">
          {HERO_PAGE_KEYS.map((pk) => {
            const raw = data?.map?.[pk];
            const disabled = raw === HERO_DISABLED_SENTINEL;
            const url = disabled ? undefined : raw;
            const busy = busyKey === pk;
            return (
              <div
                key={pk}
                className="overflow-hidden rounded-none border border-border bg-background"
              >
                <div className="aspect-[16/7] w-full bg-muted">
                  {disabled ? (
                    <div className="flex h-full w-full items-center justify-center text-[11px] text-muted-foreground">
                      الهيرو معطَّل لهذه الصفحة
                    </div>
                  ) : url ? (
                    <img
                      src={url}
                      alt={HERO_PAGE_LABELS[pk]}
                      className="h-full w-full object-cover"
                    />
                  ) : (
                    <div className="flex h-full w-full items-center justify-center text-[11px] text-muted-foreground">
                      الصورة الافتراضيّة (من الكود)
                    </div>
                  )}
                </div>
                <div className="flex flex-col gap-1.5 border-b border-border/50 px-2 py-1.5">
                  <span className="text-xs font-medium text-foreground">
                    {HERO_PAGE_LABELS[pk]}
                  </span>
                  <div className="flex flex-wrap items-center gap-1.5">
                    <Select
                      value={positionsData?.map?.[pk] ?? "middle-center"}
                      onValueChange={(v) => void handleSetPosition(pk, v as HeroPosition)}
                      disabled={busy}
                      dir="rtl"
                    >
                      <SelectTrigger className="h-7 flex-1 min-w-[140px] text-[11px]">
                        <SelectValue placeholder="موضع النصّ" />
                      </SelectTrigger>
                      <SelectContent position="popper" sideOffset={4} align="start">
                        <SelectItem value="top-start">أعلى — بداية</SelectItem>
                        <SelectItem value="top-center">أعلى — وسط</SelectItem>
                        <SelectItem value="top-end">أعلى — نهاية</SelectItem>
                        <SelectItem value="middle-start">وسط — بداية</SelectItem>
                        <SelectItem value="middle-center">وسط — وسط (افتراضي)</SelectItem>
                        <SelectItem value="middle-end">وسط — نهاية</SelectItem>
                        <SelectItem value="bottom-start">أسفل — بداية</SelectItem>
                        <SelectItem value="bottom-center">أسفل — وسط</SelectItem>
                        <SelectItem value="bottom-end">أسفل — نهاية</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <DropZone
                  onFile={(f) => void handleUpload(pk, f)}
                  disabled={busy}
                  className="px-2 pb-1"
                  title="اسحب صورة الهيرو وأفلِتها هنا"
                >
                <div className="flex items-center justify-end gap-1 px-2 py-1.5">

                    <input
                      ref={(el) => {
                        fileRefs.current[pk] = el;
                      }}
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={(e) => {
                        const f = e.target.files?.[0];
                        if (f) void handleUpload(pk, f);
                        e.target.value = "";
                      }}
                    />
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => fileRefs.current[pk]?.click()}
                      className="inline-flex items-center gap-1 rounded px-2 py-1 text-[11px] text-foreground hover:bg-muted disabled:opacity-50"
                    >
                      {busy ? (
                        <Loader2 className="h-3 w-3 animate-spin" />
                      ) : (
                        <Upload className="h-3 w-3" />
                      )}
                      {url ? "تبديل" : "رفع"}
                    </button>
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => void handleToggleDisabled(pk, disabled)}
                      className="inline-flex items-center gap-1 rounded px-2 py-1 text-[11px] text-foreground hover:bg-muted disabled:opacity-50"
                      title={disabled ? "تفعيل الهيرو" : "تعطيل الهيرو (إخفاء كلّياً)"}
                    >
                      {disabled ? <Eye className="h-3 w-3" /> : <EyeOff className="h-3 w-3" />}
                      {disabled ? "تفعيل" : "تعطيل"}
                    </button>
                    <button
                      type="button"
                      disabled={busy || !url}
                      onClick={() => setHotspotsFor({ pk, url })}
                      className="inline-flex items-center gap-1 rounded px-2 py-1 text-[11px] text-foreground hover:bg-muted disabled:opacity-50"
                      title="نقاط تفاعليّة"
                    >
                      <MapPin className="h-3 w-3" />
                      نقاط
                    </button>
                    {(url || disabled) && (
                      <button
                        type="button"
                        disabled={busy}
                        onClick={() => void handleDelete(pk)}
                        className="rounded p-1 text-destructive hover:bg-destructive/10 disabled:opacity-50"
                        title="حذف وإرجاع الافتراضيّة"
                      >
                        <Trash2 className="h-3 w-3" />
                      </button>
                    )}
                </div>
                </DropZone>
                <DropZone
                  onFile={(f) => void handlePosterUpload(pk, f)}
                  disabled={busy}
                  className="mx-2 mb-2 border-t border-border/50 pt-2"
                  title="اسحب صورة غلاف الفيديو وأفلِتها هنا"
                >
                  <div className="flex items-center justify-between gap-2 px-1 pb-1">
                    <div className="flex items-center gap-2">
                      {postersData?.map?.[pk] ? (
                        <img
                          src={postersData.map[pk]}
                          alt="غلاف الفيديو"
                          className="h-8 w-14 shrink-0 object-cover"
                        />
                      ) : null}
                      <span className="text-[11px] text-muted-foreground">
                        غلاف الفيديو (يظهر قبل التشغيل)
                      </span>
                    </div>
                    <div className="flex items-center gap-1">
                      <label className="inline-flex cursor-pointer items-center gap-1 rounded px-2 py-1 text-[11px] text-foreground hover:bg-muted">
                        <Upload className="h-3 w-3" />
                        {postersData?.map?.[pk] ? "تبديل" : "رفع"}
                        <input
                          type="file"
                          accept="image/*"
                          className="hidden"
                          disabled={busy}
                          onChange={(e) => {
                            const f = e.target.files?.[0];
                            if (f) void handlePosterUpload(pk, f);
                            e.target.value = "";
                          }}
                        />
                      </label>
                      {postersData?.map?.[pk] ? (
                        <button
                          type="button"
                          disabled={busy}
                          onClick={() => void handlePosterDelete(pk)}
                          className="rounded p-1 text-destructive hover:bg-destructive/10 disabled:opacity-50"
                          title="حذف الغلاف"
                        >
                          <Trash2 className="h-3 w-3" />
                        </button>
                      ) : null}
                    </div>
                  </div>
                </DropZone>
              </div>

            );
          })}
        </div>
      )}
      {hotspotsFor && (
        <HotspotsEditorDialog
          open={!!hotspotsFor}
          onOpenChange={(o) => !o && setHotspotsFor(null)}
          scope="page_hero"
          scopeId={hotspotsFor.pk}
          previewImageUrl={hotspotsFor.url}
          title={`نقاط: ${HERO_PAGE_LABELS[hotspotsFor.pk]}`}
        />
      )}
    </div>
  );
}

// ────────────────────────────────────────────────────────────
// قسم نصوص الهيرو العلويّ — تعديل العنوان والعنوان الفرعيّ لكلّ صفحة
// ────────────────────────────────────────────────────────────
function PageHeroTextsPanel() {
  const qc = useQueryClient();
  const getFn = useServerFn(getPageHeroTexts);
  const setFn = useServerFn(setPageHeroText);
  const { ready } = useAdminGate();

  const { data, isLoading } = useQuery({
    queryKey: ["page-hero-texts"],
    queryFn: () => getFn(),
    enabled: ready,
  });

  // مجموعات Shopify — لكلّ مجموعة محرّر نصّ هيرو خاصّ بها (`collection:<handle>`)
  const { data: collections } = useQuery({
    queryKey: ["collections", "hero-texts"],
    queryFn: () => fetchCollections(50),
    staleTime: 5 * 60_000,
  });

  const allKeys = React.useMemo(() => {
    const colKeys = (collections ?? []).map((c) => collectionHeroTextKey(c.handle));
    return [...HERO_PAGE_KEYS, ...colKeys] as string[];
  }, [collections]);

  const labelFor = React.useCallback(
    (k: string) => {
      if (k in HERO_PAGE_LABELS) return HERO_PAGE_LABELS[k as HeroPageKey];
      const handle = k.replace(/^collection:/, "");
      const col = (collections ?? []).find((c) => c.handle === handle);
      return `مجموعة: ${col?.title ?? handle} (/collection/${handle})`;
    },
    [collections],
  );

  const [drafts, setDrafts] = React.useState<Record<string, PageHeroText>>({});
  const [savingKey, setSavingKey] = React.useState<string | null>(null);

  /** النصّ الحاليّ من Shopify لمفتاح مجموعة (يُستخدم كقيمة أوّليّة عندما لا يوجد نصّ محفوظ) */
  const shopifyTextFor = React.useCallback(
    (k: string) => {
      if (!k.startsWith("collection:")) return null;
      const handle = k.replace(/^collection:/, "");
      const col = (collections ?? []).find((c) => c.handle === handle);
      if (!col) return null;
      return { title: col.title ?? "", description: col.description ?? "" };
    },
    [collections],
  );

  React.useEffect(() => {
    if (!data?.map) return;
    const next: Record<string, PageHeroText> = {};
    for (const k of allKeys) {
      const saved = data.map[k];
      const sh = saved ? null : shopifyTextFor(k);
      next[k] = {
        titleAr: saved?.titleAr ?? sh?.title ?? "",
        titleEn: saved?.titleEn ?? sh?.title ?? "",
        subtitleAr: saved?.subtitleAr ?? sh?.description ?? "",
        subtitleEn: saved?.subtitleEn ?? sh?.description ?? "",
        hidden: saved?.hidden === true,
      };
    }
    setDrafts(next);
  }, [data?.map, allKeys, shopifyTextFor]);

  const setHidden = (pk: string, value: boolean) => {
    setDrafts((prev) => ({ ...prev, [pk]: { ...prev[pk], hidden: value } }));
  };

  const setField = (pk: string, field: keyof PageHeroText, value: string) => {
    setDrafts((prev) => ({
      ...prev,
      [pk]: { ...prev[pk], [field]: value },
    }));
  };

  const isDirty = (pk: string) => {
    const d = drafts[pk];
    const s = data?.map?.[pk];
    if (!d) return false;
    if (!s) {
      // لا يوجد نصّ محفوظ بعد → أيّ محتوى في الحقول قابل للحفظ
      return Boolean(d.titleAr || d.titleEn || d.subtitleAr || d.subtitleEn || d.hidden);
    }
    return (
      d.titleAr !== (s.titleAr ?? "") ||
      d.titleEn !== (s.titleEn ?? "") ||
      d.subtitleAr !== (s.subtitleAr ?? "") ||
      d.subtitleEn !== (s.subtitleEn ?? "") ||
      (d.hidden === true) !== (s.hidden === true)
    );
  };

  async function handleSave(pk: string) {
    const d = drafts[pk];
    if (!d) return;
    setSavingKey(pk);
    try {
      await setFn({
        data: {
          pageKey: pk,
          titleAr: d.titleAr,
          titleEn: d.titleEn,
          subtitleAr: d.subtitleAr,
          subtitleEn: d.subtitleEn,
          hidden: d.hidden === true,
        },
      });
      await qc.invalidateQueries({ queryKey: ["page-hero-texts"] });
      toast.success("تمّ حفظ النصوص");
    } catch (e) {
      toast.error("فشل الحفظ: " + (e instanceof Error ? e.message : String(e)));
    } finally {
      setSavingKey(null);
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 text-xs text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" /> جاري التحميل…
      </div>
    );
  }

  return (
    <div className="mb-4 rounded-none border border-border bg-card p-4">
      <div className="mb-3 flex items-center justify-between">
        <h2 className="text-sm font-semibold text-foreground">نصوص الهيرو العلويّ</h2>
        <span className="text-[11px] text-muted-foreground">العنوان والعنوان الفرعيّ لكلّ صفحة</span>
      </div>
      <p className="mb-4 text-[11px] text-muted-foreground">
        اترك الحقل فارغاً للعودة للنصّ الافتراضيّ من ملفّ الترجمة. النصّ المحفوظ هنا يتقدّم على النصّ الافتراضيّ.
      </p>
      <div className="space-y-4">
        {allKeys.map((pk) => {
          const d = drafts[pk];
          const dirty = isDirty(pk);
          const busy = savingKey === pk;
          return (
            <Card key={pk} className="rounded-none border-border">
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center justify-between text-sm">
                  <span>{labelFor(pk)}</span>
                  <Button
                    size="sm"
                    disabled={!dirty || busy}
                    onClick={() => void handleSave(pk)}
                  >
                    {busy ? <Loader2 className="me-1 h-3.5 w-3.5 animate-spin" /> : null}
                    {busy ? "جارٍ الحفظ…" : "حفظ"}
                  </Button>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <label className="flex items-center gap-2 rounded-none border border-border bg-muted/30 px-3 py-2 text-xs text-foreground">
                  <Switch
                    checked={d?.hidden === true}
                    onCheckedChange={(v) => setHidden(pk, v)}
                  />
                  <span>إخفاء النصّ كليّاً من هذه الصفحة (لا يعود للنصّ الافتراضيّ)</span>
                </label>
                <div className={`grid gap-4 md:grid-cols-2 ${d?.hidden ? "pointer-events-none opacity-50" : ""}`}>
                <div className="space-y-3">
                  <div className="space-y-1.5">
                    <Label className="text-xs">العنوان (عربي)</Label>
                    <Input
                      value={d?.titleAr ?? ""}
                      onChange={(e) => setField(pk, "titleAr", e.target.value)}
                      placeholder="مثال: المجموعة"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs">العنوان الفرعيّ (عربي)</Label>
                    <Textarea
                      rows={2}
                      value={d?.subtitleAr ?? ""}
                      onChange={(e) => setField(pk, "subtitleAr", e.target.value)}
                      placeholder="مثال: اكتشف عطوراً صُنعت لكلّ لحظة"
                    />
                  </div>
                </div>
                <div className="space-y-3" dir="ltr">
                  <div className="space-y-1.5">
                    <Label className="text-xs">Title (English)</Label>
                    <Input
                      dir="ltr"
                      lang="en"
                      value={d?.titleEn ?? ""}
                      onChange={(e) => setField(pk, "titleEn", e.target.value)}
                      placeholder="e.g. The Collection"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs">Subtitle (English)</Label>
                    <Textarea
                      dir="ltr"
                      lang="en"
                      rows={2}
                      value={d?.subtitleEn ?? ""}
                      onChange={(e) => setField(pk, "subtitleEn", e.target.value)}
                      placeholder="e.g. Discover perfumes made for every moment"
                    />
                  </div>
                </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}


